import { useQueryClient } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useState
} from 'react';

import { PaymentProviders } from 'payments/constants/providers';
import { handleSaveCardOnFileMutation } from 'payments/helpers/handleSaveCardOnFileMutation';
import {
  CommerceHubCredentialsResponse,
  getPaymentCredentials
} from 'payments/queries/paymentCredentials';
import { CommerceHubPaymentForm } from 'payments/types/commerceHub';
import type {
  PartialSaveOptions,
  PaymentFormContextWithPostalCodeInterface,
  SaveCardOnFileMutation
} from 'payments/types/form';

const CommerceHubPaymentFormContext =
  createContext<PaymentFormContextWithPostalCodeInterface | null>(null);

type ProviderProps = {
  handleCardSubmit: CommerceHubPaymentForm['submit'];
};

export function CommerceHubPaymentFormProvider({
  handleCardSubmit,
  children
}: PropsWithChildren<ProviderProps>) {
  // NOTE: Although CommerceHub doesn't require a Zip Code, we currently do take
  // the zip code as an input anyway from the diner.
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeError, setPostalCodeError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const saveCardOnFile = async (
    saveOptions: PartialSaveOptions,
    mutate: SaveCardOnFileMutation
  ) => {
    if (!postalCode) {
      setPostalCodeError('Zip is required');
      return;
    } else {
      setPostalCodeError(null);
    }

    const data = (await queryClient.fetchQuery(
      getPaymentCredentials(PaymentProviders.COMMERCE_HUB)
    )) as CommerceHubCredentialsResponse;

    if (Object.values(data).some(value => !value)) {
      throw new Error('CommerceHub credentials are empty');
    }

    const submission = await handleCardSubmit({
      apiKey: data.commerce_hub_api_key,
      accessToken: data.commerce_hub_access_token,
      publicKey: data.commerce_hub_public_key,
      merchantId: data.commerce_hub_merchant_id,
      terminalId: data.commerce_hub_terminal_id,
      keyId: data.commerce_hub_key_id
    });

    if (!submission) {
      throw new Error(
        'CommerceHub form data submission for card capture is null'
      );
    }

    handleSaveCardOnFileMutation(
      saveOptions,
      mutate,
      data.commerce_hub_client_request_id
    );
    return;
  };

  return (
    <CommerceHubPaymentFormContext.Provider
      value={{
        saveCardOnFile,
        postalCode,
        setPostalCode,
        postalCodeError,
        setPostalCodeError
      }}
    >
      {children}
    </CommerceHubPaymentFormContext.Provider>
  );
}

export function useCommerceHubPaymentForm() {
  return useContext(CommerceHubPaymentFormContext);
}
